import ReactDOM from "react-dom";
import React, { useState } from "react";
import * as $ from "jquery";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as moment from "moment";
import * as _ from "lodash";
import bootbox from "bootbox";

import { UiUtils } from "../../common/uiUtils";
import { ViewerUtils } from "../../common/viewerUtils";
import { ModelApi } from "../../api/modelApi";
import { viewerOptions, viewerState } from "../../viewer";
import * as SmartDrawings from "../../smartDrawings";
import { Misc } from "../../common/misc";
import { MarkupPanel } from "./markupPanel";

interface MarkupListProps {
    markups: SmartDrawings.Markup[];
    selectedMarkupId: string;
    openMarkup: (id) => void;
    addMarkup: () => void;
    removeMarkup: () => void;
    removeAllMarkups: () => void;
    selectMarkup: (id) => void;
    viewer: Autodesk.Viewing.GuiViewer3D;
}

interface MarkupProps {
    markup: SmartDrawings.Markup;
    selected: boolean;
    onClick: () => void;
    onDoubleClick: () => void;
}

interface MarkupUiProps {
    viewer: Autodesk.Viewing.GuiViewer3D;
}

interface MarkupUiState {
    markups: { [key: string]: SmartDrawings.Markup; };
    selectedMarkupId: string;
}

const MarkupRow: React.SFC<MarkupProps> = props => {

    return <li className="comment-list-item" style={props.selected ? { border: "solid white" } : null}
        onClick={props.onClick}
        onDoubleClick={props.onDoubleClick}>
               <div className="comment-list-label">{props.markup.name || "<No name>"}</div>
           </li>;

}

const MarkupList: React.SFC<MarkupListProps> = (props) => {
    return <div>
               <ul className="plain-list" style={{ height: "150px", overflow: "hidden", overflowY: "scroll" }}>
                   {props.markups.map(c => {
                       return <MarkupRow
                           key={c.id}
                           markup={c}
                           selected={c.id === props.selectedMarkupId}
                           onClick={() => props.selectMarkup(c.id)}
                           onDoubleClick={() => props.openMarkup(c.id)} />;
                   })}
        </ul>

        <button className="btn btn-success ml-1" onClick={() => props.openMarkup(props.selectedMarkupId)} disabled={props.selectedMarkupId === null}>
                   Open
               </button>
               <button className="btn btn-primary ml-1" onClick={props.addMarkup}>
                   Add
               </button>
               <button className="btn btn-primary ml-1" onClick={props.removeMarkup} disabled={props.selectedMarkupId === null}>
                   Remove
               </button>
               <button className="btn btn-primary ml-1" onClick={props.removeAllMarkups} disabled={_.isEmpty(props.markups)}>
                   Remove all
               </button>
           </div>;
}

class MarkupUi extends React.Component<MarkupUiProps, MarkupUiState> {

    private markupPanel: MarkupPanel;

    constructor(props) {
        super(props);
        this.state = {
            markups: {},
            selectedMarkupId: null
        }
        this.markupPanel = new MarkupPanel(props.viewer, props.viewer.container, "SmartDrawings-Panel-Markup", this.onMarkupClose);
    }

    componentDidMount(): void {
        this.removeAllMarkups();
        if (!_.isEmpty(viewerState.markups)) {
            this.setState({ markups: viewerState.markups });
            this.selectMarkup(_.first(_.values(viewerState.markups)).id);
        };
    }

    componentWillUnmount(): void {
        this.selectMarkup(null);
    }

    componentDidUpdate(prevProps: { readonly [P in "viewer"]: MarkupUiProps[P] },
        prevState: { readonly [P in "markups" | "selectedMarkupId"]: MarkupUiState[P] },
        snapshot?): void {
        viewerState.markups = this.state.markups;
    }

    onMarkupClose = () => {
        this.setState({ markups: viewerState.markups });
    }

    selectMarkup = (id) => {
        this.setState({ selectedMarkupId: id });
    }

    openMarkup = (markup) => {
        //(this.props.viewer.utilities as any).goHome();
        ViewerUtils.applyViewState(this.props.viewer, markup);
        this.markupPanel.setMarkupVisible(true, markup);
    }

    addMarkup = () => {
        const newMarkup: SmartDrawings.Markup = {
            id: Misc.guid(),
            name: "",
            svg: null,
            posVector: this.props.viewer.navigation.getPosition().toModelUnits().toSD(),
            pivotVector: this.props.viewer.navigation.getPivotPoint().toModelUnits().toSD(),
            rotationQuaternion: ViewerUtils.quaternionToObject(this.props.viewer.navigation.getCamera().quaternion)
        };
        const updatedMarkups = { ...this.state.markups };
        updatedMarkups[newMarkup.id] = newMarkup;
        this.setState({ markups: updatedMarkups });
        this.selectMarkup(newMarkup.id);
        this.openMarkup(newMarkup);
    }

    removeMarkup = () => {
        const updatedMarkups = { ...this.state.markups };
        delete updatedMarkups[this.state.selectedMarkupId];
        this.setState({ markups: updatedMarkups, selectedMarkupId: null });
    }

    removeAllMarkups = () => {
        this.setState({ markups: {} });
    }

    updateMarkup = (name, text) => {
        const selectedId = this.state.selectedMarkupId;
        const updatedMarkups = { ...this.state.markups };
        const markup = updatedMarkups[selectedId];
        markup.name = name || markup.name;
  
        updatedMarkups[selectedId] = markup;
        this.setState({ markups: updatedMarkups });
    }

    render() {
        const markups = _.values(this.state.markups);
        const selectedMarkup = this.state.markups[this.state.selectedMarkupId];
        return <div id="comment-react" className="react-content">
            <MarkupList
                viewer={this.props.viewer}
                markups={markups}
                selectedMarkupId={this.state.selectedMarkupId}
                openMarkup={id => this.openMarkup(this.state.markups[id])}
                addMarkup={this.addMarkup}
                removeMarkup={this.removeMarkup}
                removeAllMarkups={this.removeAllMarkups}
                selectMarkup={this.selectMarkup} />
               </div>;
    }

}

class MarkupListPanel extends Autodesk.Viewing.UI.DockingPanel {

    private viewer: Autodesk.Viewing.GuiViewer3D;

    private contentDiv: HTMLDivElement;
    private reactNode;
  
    constructor(viewer: Autodesk.Viewing.GuiViewer3D, container: any, id: string, options?: any) {
        super(container, id, "Markups", options);
        this.viewer = viewer;
        this.container.classList.add("markup-list-panel");

        this.contentDiv = document.createElement("div");
        this.contentDiv.classList.add("content");
        this.container.appendChild(this.contentDiv);
    }

    setVisible(show: boolean) {
        super.setVisible(show);
        if (show) {
            ReactDOM.unmountComponentAtNode(this.contentDiv);
            this.reactNode = ReactDOM.render(<MarkupUi viewer={this.viewer} />, this.contentDiv);
        } else if (this.reactNode) {
            ReactDOM.unmountComponentAtNode(this.contentDiv);
            this.reactNode = null;
        }
    }
}

export { MarkupListPanel };